import { useCallback, useEffect, useState } from 'react';
import * as api from '../api';
import PageHeader from '../components/ui/PageHeader';
import PageCard from '../components/ui/PageCard';
import DataTable from '../components/ui/DataTable';
import FormField from '../components/ui/FormField';
import ErrorMessage from '../components/ui/ErrorMessage';
import Loader from '../components/ui/Loader';
import './PricingPage.scss';

export default function PricingPage() {
    const [anchors, setAnchors] = useState([]);
    const [preview, setPreview] = useState([]);
    const [deviceMin, setDeviceMin] = useState(2);
    const [deviceMax, setDeviceMax] = useState(10);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await api.getPricingAnchors();
            setAnchors((data.anchors || []).map((a) => ({
                devices: String(a.devices),
                monthly_price: String(a.monthly_price),
            })));
            setPreview(data.preview || []);
            setDeviceMin(data.device_min ?? 2);
            setDeviceMax(data.device_max ?? 10);
        } catch (err) {
            setError(err.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleAnchorChange = (index, field) => (e) => {
        setAnchors((prev) => prev.map((item, i) => (
            i === index ? { ...item, [field]: e.target.value } : item
        )));
        setSuccess('');
    };

    const addAnchor = () => {
        setAnchors((prev) => [...prev, { devices: '', monthly_price: '' }]);
    };

    const removeAnchor = (index) => {
        setAnchors((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = anchors.map((a) => ({
                devices: Number(a.devices),
                monthly_price: a.monthly_price,
            }));
            const data = await api.putPricingAnchors(payload);
            setAnchors((data.anchors || []).map((a) => ({
                devices: String(a.devices),
                monthly_price: String(a.monthly_price),
            })));
            setPreview(data.preview || []);
            setSuccess('Цены сохранены');
        } catch (err) {
            setError(err.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Loader text="Загружаем цены..." />;
    }

    const previewColumns = [
        { key: 'devices', title: 'Устройств' },
        { key: 'monthly_price', title: 'Цена/мес' },
    ];

    return (
        <div className="pricing-page">
            <PageHeader
                title="Цены"
                subtitle={`Якорные цены для ${deviceMin}–${deviceMax} устройств`}
            />

            <ErrorMessage message={error} onRetry={load} />
            {success && <div className="form-banner">{success}</div>}

            <PageCard className="pricing-page__anchors">
                <div className="pricing-page__head">
                    <h3>Якорные точки</h3>
                    <button type="button" className="btn btn--outline btn--sm" onClick={addAnchor}>
                        Добавить якорь
                    </button>
                </div>

                <div className="pricing-page__anchor-list">
                    {anchors.map((anchor, index) => (
                        <div key={index} className="pricing-page__anchor-row">
                            <FormField
                                label="Устройств"
                                type="number"
                                value={anchor.devices}
                                onChange={handleAnchorChange(index, 'devices')}
                            />
                            <FormField
                                label="Цена в месяц"
                                value={anchor.monthly_price}
                                onChange={handleAnchorChange(index, 'monthly_price')}
                            />
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm pricing-page__remove"
                                onClick={() => removeAnchor(index)}
                            >
                                Удалить
                            </button>
                        </div>
                    ))}
                </div>

                <button type="button" className="btn btn--primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить якоря'}
                </button>
            </PageCard>

            <PageCard className="pricing-page__preview">
                <h3>Предпросмотр таблицы</h3>
                <DataTable columns={previewColumns} rows={preview} emptyMessage="Нет данных" />
            </PageCard>
        </div>
    );
}
